import { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import { CountrySelector, usePhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { cva } from 'class-variance-authority';
import { cn } from 'Utils/cn';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import FloatLabel from '../FloatLabel/FloatLabel';

const wrapperVariants = cva(
    'flex w-full items-stretch rounded-md border border-gray-200 bg-white shadow-sm transition-all md:text-sm',
    {
        variants: {
            size: {
                normal: 'h-11',
                small: 'h-8 md:text-xs',
                large: 'h-14 md:text-base',
            },
        },
        defaultVariants: {
            size: 'normal',
        },
    },
);

const InputTel = forwardRef(function InputTel(props, ref) {
    const {
        className,
        label,
        value = '',
        onChange,
        onBlur,
        defaultCountry = 'es',
        disabled,
        error,
        hasError: hasErrorProp,
        autoFocus,
        name,
        placeholder,
        size,
        ...rest
    } = props;

    const hasError = hasErrorProp || !!error;
    const [isFocused, setIsFocused] = useState(false);

    const { inputValue, handlePhoneValueChange, country, setCountry } = usePhoneInput({
        defaultCountry,
        value,
        onChange: (data) => {
            onChange?.(data.phone);
        },
    });

    return (
        <div className={cn('w-full', className)}>
            <div className="relative w-full group">
                <FloatLabel label={label} hasError={hasError} highlight={false} />

                <div
                    className={cn(
                        wrapperVariants({ size }),
                        isFocused && 'border-primary ring-2 ring-primary/20',
                        hasError && 'border-danger',
                        disabled && 'cursor-not-allowed opacity-50',
                    )}
                >
                    <CountrySelector
                        selectedCountry={country.iso2}
                        onSelect={({ iso2 }) => setCountry(iso2)}
                        disabled={disabled}
                        buttonStyle={{
                            height: '100%',
                            border: 0,
                            borderRight: '1px solid #e5e7eb',
                            borderRadius: 0,
                            backgroundColor: 'transparent',
                            padding: '0 8px',
                        }}
                        dropdownStyleProps={{
                            style: {
                                zIndex: 'var(--z-index-dropdown)',
                                borderRadius: '0.375rem',
                                boxShadow: '0 6px 14px rgb(0 0 0 / 15%)',
                                '--react-international-phone-dropdown-item-height': '36px',
                                '--react-international-phone-selected-dropdown-item-background-color':
                                    'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    />

                    <input
                        ref={ref}
                        type="tel"
                        name={name}
                        value={inputValue}
                        disabled={disabled}
                        autoFocus={autoFocus}
                        placeholder={placeholder}
                        onChange={handlePhoneValueChange}
                        onFocus={() => setIsFocused(true)}
                        onBlur={(e) => {
                            setIsFocused(false);
                            onBlur?.(e);
                        }}
                        className="h-full flex-1 min-w-0 bg-transparent px-3 text-dark-blue placeholder:text-dark-blue/50 outline-none"
                        {...rest}
                    />
                </div>

                <ErrorMessage message={error} />
            </div>
        </div>
    );
});

InputTel.propTypes = {
    className: PropTypes.string,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    value: PropTypes.string,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    defaultCountry: PropTypes.string,
    disabled: PropTypes.bool,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    hasError: PropTypes.bool,
    autoFocus: PropTypes.bool,
    name: PropTypes.string,
    placeholder: PropTypes.string,
    size: PropTypes.oneOf(['normal', 'small', 'large']),
};

export const matchIsValidTel = (value) => {
    if (!value) return false;
    const trimmed = String(value).replace(/\s/g, '');
    if (!/^\+\d+$/.test(trimmed)) return false;
    const digits = trimmed.slice(1);
    return digits.length >= 7 && digits.length <= 15;
};

export default InputTel;
